import React from 'react';
import styled, { useTheme } from 'styled-components';
import { TitleLarge, TitleMedium } from '../../../components/Texts';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import TextInput from '../../../components/Inputs/Text';
import Button from '../../../components/Button';
import { useNotifyAppUsersBulkMutation } from '../../../redux/api/authorized/bulkNotifications';
import { toast } from 'react-toastify';

const Container = styled.div`
  width: 100%;
  min-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NotificationBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.border.primary};
  background-color: ${({ theme }) => theme.background.primary};
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`;

const NotificationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.regular};
  margin: 0;
`;

const RemoveButton = styled.button`
  background-color: ${({ theme }) => theme.background.light};
  color: ${({ theme }) => theme.text.error};
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.text.error};
    color: ${({ theme }) => theme.background.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const notificationSchema = yup.object({
  userId: yup
    .number()
    .typeError('User ID must be a number')
    .required('User ID is required')
    .integer('User ID must be an integer')
    .positive('User ID must be positive'),
  title: yup.string().required('Title is required'),
  body: yup.string().required('Body is required'),
  clickAction: yup
    .string()
    .url('Click action must be a valid URL')
    .required('Click action is required'),
});

const schema = yup.object({
  notifications: yup
    .array()
    .of(notificationSchema)
    .min(1, 'At least one notification is required')
    .required(),
});

const BulkNotification = () => {
  const theme = useTheme();
  const [notifyAppUsersBulk, { isLoading: isSubmitting }] =
    useNotifyAppUsersBulkMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      notifications: [
        {
          userId: '',
          title: '',
          body: '',
          clickAction: '',
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'notifications',
  });

  const onSubmit = async (data) => {
    try {
      // Convert userId to number and trim strings
      const notifications = data.notifications.map((notification) => {
        const payload = {
          userId: parseInt(notification.userId, 10),
          title: notification.title,
          body: notification.body,
          clickAction: notification.clickAction.trim(),
        };

        return payload;
      });

      const response = await notifyAppUsersBulk({ notifications }).unwrap();

      toast('Bulk notifications sent successfully!', {
        type: 'success',
      });
    } catch (error) {
      console.error('Error sending bulk notifications:', error);
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Failed to send bulk notifications';
      toast(errorMessage, {
        type: 'error',
      });
    }
  };

  const addNewNotification = () => {
    append({
      userId: '',
      title: '',
      body: '',
      clickAction: '',
    });
  };

  return (
    <Container>
      <TitleLarge>Bulk Custom Notifications</TitleLarge>
      <TitleMedium>
        Send custom push notifications to multiple users at once
      </TitleMedium>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          marginTop: 20,
          gap: 20,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {fields.map((field, index) => (
          <NotificationBox key={field.id}>
            <NotificationHeader>
              <NotificationTitle>
                Notification {index + 1}
              </NotificationTitle>
              {fields.length > 1 && (
                <RemoveButton
                  type='button'
                  onClick={() => remove(index)}
                  disabled={isSubmitting}
                >
                  Remove
                </RemoveButton>
              )}
            </NotificationHeader>
            <FormContainer>
              <Controller
                name={`notifications.${index}.userId`}
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='User ID'
                    type='number'
                    placeholder='e.g., 123'
                    error={errors?.notifications?.[index]?.userId?.message}
                    {...field}
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value, 10) || '')
                    }
                  />
                )}
              />
              <Controller
                name={`notifications.${index}.title`}
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Title'
                    type='text'
                    placeholder='e.g., Hello'
                    error={errors?.notifications?.[index]?.title?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name={`notifications.${index}.body`}
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Body'
                    type='text'
                    placeholder='e.g., This is a test notification'
                    error={errors?.notifications?.[index]?.body?.message}
                    {...field}
                  />
                )}
              />
              <Controller
                name={`notifications.${index}.clickAction`}
                control={control}
                render={({ field }) => (
                  <TextInput
                    label='Click Action URL'
                    type='url'
                    placeholder='e.g., https://deks.xyz/'
                    error={
                      errors?.notifications?.[index]?.clickAction?.message
                    }
                    {...field}
                  />
                )}
              />
            </FormContainer>
          </NotificationBox>
        ))}

        <AddButtonContainer>
          <Button
            type='button'
            variant='secondary'
            onClick={addNewNotification}
            disabled={isSubmitting}
          >
            Add New Notification
          </Button>
        </AddButtonContainer>

        <ButtonContainer>
          <Button
            type='submit'
            loading={isSubmitting}
            disabled={!isValid || isSubmitting}
          >
            Send Notifications
          </Button>
        </ButtonContainer>
      </form>
    </Container>
  );
};

export default BulkNotification;
